/**
 * useMealPlan - Comprehensive hook for managing meal plans
 *
 * This hook combines all meal plan functionality for both PRO editor
 * and consumer dashboard contexts. It orchestrates meal management,
 * nutrition analysis, permissions, and save state.
 *
 * Features:
 * - Complete meal plan state management
 * - Integrates meal operations, nutrition, and modals
 * - Handles both virtual (PRO) and saved (consumer) plans
 * - Permission-aware operations
 * - Auto-save and dirty state tracking
 * - Undo/redo support
 */
import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { v4 as uuid } from 'uuid';
import { usePlanAnalysis } from './usePlanAnalysis';
import { useMealManagement } from './useMealManagement';
import { usePlanDates } from './usePlanDates';
import { useModalManager } from './useModalManager';
import { useVirtualPlan } from './useVirtualPlan';
import { useSchedulePlan } from './useSchedulePlan';
import { usePlanLoading } from './usePlanLoading';
import { usePlanSaving } from './usePlanSaving';
import { getAssetsForMeals, getPlanMealTypes } from '@utils/Meals';
import confirm from '@utils/Confirm';

/**
 * Configuration for meal plan management
 * @typedef {Object} MealPlanConfig
 * @property {Object} initialPlan - Initial plan data
 * @property {Object} profile - User/patient profile
 * @property {Object} permissions - Plan permissions (PRO only)
 * @property {boolean} autoSave - Enable auto-save
 * @property {number} autoSaveDelay - Auto-save delay in ms
 * @property {boolean} enableUndo - Enable undo/redo
 * @property {number} maxUndoSteps - Maximum undo history
 * @property {Object} router - Router instance for modals
 * @property {Function} showUpgradeForm - Function to show upgrade/subscription form
 * @property {Function} showLoginForm - Function to show login form
 * @property {boolean} isCordova - Whether running in Cordova app
 */

/**
 * Main meal plan management hook
 *
 * @param {MealPlanConfig} config - Configuration object
 * @returns {Object} Complete meal plan management interface
 */
export const useMealPlan = (config = {}) => {
    const {
        planId = null,           // Plan UUID to load
        initialPlan = null,      // Initial plan data (for testing)
        profile = {},
        user = null,             // User object
        autoSave = false,
        autoSaveDelay = 3000,
        enableUndo = false,
        maxUndoSteps = 20,
        router = null,
        envelope = null,         // Nutrient targets/envelope
        showUpgradeForm = null,
        showLoginForm = null,
        isCordova = false,
        onPlanUuidChanged = null, // Callback when plan UUID changes
    } = config;

    // Virtual plan management - moved inside the hook
    const virtualPlanHandler = useVirtualPlan(planId);

    // Consolidated plan loading - handles all loading concerns (no currentPlan passed to avoid loops)
    const planLoading = usePlanLoading(planId, virtualPlanHandler, initialPlan, profile);

    // Create stable plan object - use loaded plan or empty plan as fallback
    const plan = useMemo(() => {
        return planLoading.plan || createEmptyPlan();
    }, [planLoading.plan, profile.uuid, profile.portion]);

    // Create stable references to prevent infinite loops
    const stablePlanLoadingSetPlan = useCallback((newPlan) => {
        planLoading.setPlan(newPlan);
    }, [planLoading.setPlan]);

    // Memoize onPlanSaved callback to prevent recreation on every render
    const onPlanSavedCallback = useCallback(async (newPlan) => {
        // Update plan state when save is successful
        const oldUuid = plan?.uuid;
        const newUuid = newPlan.uuid;

        // Check if this was a virtual plan that became real
        const wasVirtual = !plan?.links?.self;
        const isNowReal = !!newPlan.links?.self;

        stablePlanLoadingSetPlan(newPlan);

        // Re-fetch permissions when virtual plan becomes real
        // This ensures user gets write permission on their newly created plan
        //
        // Background: When a virtual plan is first loaded, permissions are checked
        // against the virtual UUID, which may return canWrite=false for non-admins.
        // After saving, the plan becomes real and the user owns it, so they should
        // have write permission. Without refetching, the save button would disappear
        // if the user makes additional edits after the first save.
        if (wasVirtual && isNowReal && planLoading.refetchPermissions) {
            await planLoading.refetchPermissions(newUuid);
        }

        if (oldUuid !== newUuid) {
            onPlanUuidChanged(newUuid, oldUuid);
        }
    }, [plan?.uuid, plan?.links?.self, stablePlanLoadingSetPlan, onPlanUuidChanged, planLoading.refetchPermissions]);

    // Consolidated plan saving - handles all saving concerns
    const planSaving = usePlanSaving({
        plan,
        virtualPlanHandler,
        permissions: planLoading.permissions,
        profile,
        user,
        isDietitianRecommended: planLoading.isDietitianRecommended,
        autoSave,
        autoSaveDelay,
        onPlanSaved: onPlanSavedCallback,
        onPlanUuidChanged
    });

    // Create stable references for frequently used methods
    const stableMarkDirty = useCallback(() => {
        planSaving.saveState.markDirty();
    }, [planSaving.saveState.markDirty]);

    const stableAutoSaveToLocal = useCallback((planData) => {
        planSaving.autoSaveToLocal(planData);
    }, [planSaving.autoSaveToLocal]);

    // Handle marking dirty when plan loads from local storage
    useEffect(() => {
        if (planLoading.shouldMarkDirty && !planLoading.loading) {
            stableMarkDirty();
        }
    }, [planLoading.shouldMarkDirty, planLoading.loading, stableMarkDirty]);

    // Create empty plan structure
    function createEmptyPlan() {
        return {
            uuid: uuid(),
            name: 'New Meal Plan',
            items: [],
            participants: [{ uuid: 'USER', portion: profile.portion || 1 }],
            type: 'plan'
        };
    }

    // Undo/redo state
    const [undoStack, setUndoStack] = useState([]);
    const [redoStack, setRedoStack] = useState([]);

    // Max offset state (independent of meals after initial load)
    const [maxOffset, setMaxOffset] = useState(0);

    // Nutrition recalculation trigger - increments to force nutrition re-analysis
    const [nutritionKey, setNutritionKey] = useState(0);

    // Date management
    const planDates = usePlanDates({
        meals: plan.items || [],
        defaultDuration: 7
    });

    /**
     * Get meal types to render
     *
     * Always show meal type rows regardless of meal content
     */
    const mealTypes = useMemo(() => getPlanMealTypes(plan, profile), [plan, profile]);

    const nutrition = usePlanAnalysis({
        meals: plan.items || [],
        assets: plan.assets || null, // Virtual plans won't have assets initially
        profile,
        plan,
        mealTypes,
        nutritionKey // Force recalculation when this changes
    });

    /**
     * Refresh assets and re-analyze after food/recipe edits.
     * The editors already update _cache.documents via updateCachedDocuments(),
     * so we just need to re-fetch from the (now-fresh) cache and re-analyze.
     */
    const refreshAfterEdit = useCallback(() => {
        setNutritionKey(prev => prev + 1);
        nutrition.analyzeWithBackend();
    }, [nutrition.analyzeWithBackend]);

    // Modal management - pure state, no business logic
    const modalManager = useModalManager({
        permissions: planLoading.permissions
    });

    const mealManagement = useMealManagement({
        meals: plan.items || [],
        onMealsChange: (newMeals) => updatePlan({ items: newMeals }),
        profile,
        assets: nutrition.assets,
        currentDate: planDates.startDate,
        trackHistory: enableUndo,
        modalManager,
        plan
    });

    // Schedule plan functionality
    const schedulePlan = useSchedulePlan({
        plan,
        profile,
        user,
        router,
        modalManager,
        showUpgradeForm,
        showLoginForm,
        isCordova,
        synced: !planSaving.isSaving && planSaving.saveState.isOnline,
        recipes: nutrition.assets.recipes,
        foods: nutrition.assets.foods,
        savePlan: planSaving.savePlan, // Use the save function from the saving hook
        onComplete: (planMarker) => {
            console.log('Plan scheduled successfully:', planMarker);
        }
    });

    /**
     * Update plan with undo support
     */
    const updatePlan = useCallback((updates) => {
        // Save to undo stack if enabled
        if (enableUndo) {
            setUndoStack(prev => {
                const newStack = [...prev, plan];
                return newStack.slice(-maxUndoSteps); // Limit stack size
            });
            setRedoStack([]); // Clear redo on new change
        }

        const newPlan = {
            ...plan,
            ...updates
        };

        // Dirty State Handling for Soft Delete:
        // Only mark dirty if this is NOT a soft-delete operation
        // Soft delete = meals marked as deleted:true (undoable)
        // Final cleanup = meals actually removed (should mark dirty)
        let shouldMarkDirty = true;

        if (updates.items) {
            const oldMeals = plan.items || [];
            const newMeals = updates.items;

            // Check if this is a soft delete: same count, some meals gained deleted:true
            const isSoftDelete = (
                oldMeals.length === newMeals.length &&
                newMeals.some(meal => meal.deleted) &&
                !oldMeals.every((oldMeal, idx) => newMeals[idx]?.deleted === oldMeal.deleted)
            );

            // Check if this is a restore: meals losing deleted:true flag
            const isRestore = (
                oldMeals.length === newMeals.length &&
                oldMeals.some(meal => meal.deleted) &&
                !newMeals.some(meal => meal.deleted)
            );

            // Don't mark dirty for soft deletes or restores (both are undoable)
            if (isSoftDelete || isRestore) {
                shouldMarkDirty = false;
            }
        }

        if (shouldMarkDirty) {
            stableMarkDirty();
        }

        // Auto-save to local storage
        stableAutoSaveToLocal(newPlan);

        // Force nutrition recalculation if meals changed
        if (updates.items) {
            setNutritionKey(prev => prev + 1);
        }

        // Update the plan in the loading hook
        stablePlanLoadingSetPlan(newPlan);
    }, [enableUndo, maxUndoSteps, plan, stableMarkDirty, stableAutoSaveToLocal, stablePlanLoadingSetPlan]);

    /**
     * Undo last change
     */
    const undo = useCallback(() => {
        if (undoStack.length === 0) return;

        const previousPlan = undoStack[undoStack.length - 1];
        setUndoStack(prev => prev.slice(0, -1));
        setRedoStack(prev => [...prev, plan]);
        stablePlanLoadingSetPlan(previousPlan);
        stableMarkDirty();
    }, [undoStack, plan, stablePlanLoadingSetPlan, stableMarkDirty]);

    /**
     * Redo last undone change
     */
    const redo = useCallback(() => {
        if (redoStack.length === 0) return;

        const nextPlan = redoStack[redoStack.length - 1];
        setRedoStack(prev => prev.slice(0, -1));
        setUndoStack(prev => [...prev, plan]);
        stablePlanLoadingSetPlan(nextPlan);
        stableMarkDirty();
    }, [redoStack, plan, stablePlanLoadingSetPlan, stableMarkDirty]);

    /**
     * Clear all meals
     */
    const clearPlan = useCallback(async () => {
        const shouldClear = await confirm('Are you sure you want to clear all meals?');
        if (shouldClear) {
            updatePlan({ items: [] });
        }
    }, [updatePlan]);

    /**
     * Add a day to the meal plan (increment maxOffset)
     */
    const addDay = useCallback(() => {
        setMaxOffset(prev => prev + 1);
        stableMarkDirty();

        return true;
    }, [maxOffset, stableMarkDirty]);


    /**
     * Initialize maxOffset from meals when plan loads (only once per plan)
     */
    useEffect(() => {
        const computedMaxOffset = plan.items.length === 0
            ? 0
            : Math.max(...plan.items.map(item => item.offset || 0));

        setMaxOffset(computedMaxOffset);
    }, [plan.uuid]); // Only reset when plan UUID changes (new plan loaded)

    // Grow maxOffset when items introduce a higher offset (e.g. leftover added to a new day)
    useEffect(() => {
        if (plan.items.length === 0) return;
        const computedMaxOffset = Math.max(...plan.items.map(item => item.offset || 0));
        setMaxOffset(prev => Math.max(prev, computedMaxOffset));
    }, [plan.items]);

    /**
     * Get plan summary
     */
    const summary = useMemo(() => {
        const activeMeals = plan.items.filter(m => !m.deleted);
        const mealsByType = {};

        activeMeals.forEach(meal => {
            const type = meal.meal || 'Other';
            mealsByType[type] = (mealsByType[type] || 0) + 1;
        });

        return {
            totalMeals: activeMeals.length,
            mealsByType,
            totalCalories: nutrition.analysis?.nutrients?.['208'] || 0,
            totalProtein: nutrition.analysis?.nutrients?.['203'] || 0,
            participantCount: plan.participants?.length || 1,
            hasLeftovers: activeMeals.some(m => m.meal_type === 'leftover'),
            meetsRequirements: false
        };
    }, [plan, nutrition.analysis?.nutrients]);


    // Groceries are now calculated directly by the nutrition hook
    const groceries = nutrition.groceries;


    return {
        // Plan data
        plan,
        summary,
        mealTypes,
        maxOffset,

        // Plan operations
        updatePlan,
        savePlan: planSaving.savePlan,
        duplicatePlan: planSaving.duplicatePlan,
        clearPlan,
        addDay,

        // Meal operations
        mealManager: mealManagement,
        ...mealManagement,

        // Nutrition data
        nutrition,
        refreshAfterEdit,

        // Assets (recipes, foods, etc.)
        assets: nutrition.assets,

        // Grocery calculations
        groceries,

        // Modal management
        modalManager,

        // Date management
        planDates,

        // Save state (includes justSaved)
        saveState: planSaving.saveState,

        // Success message state (from saveState for convenience)
        justSaved: planSaving.saveState.justSaved,

        // Duplicate state
        isDuplicating: planSaving.isDuplicating,

        // Loading states
        loading: planLoading.loading,
        error: planLoading.error,

        // Undo/redo
        undo: enableUndo ? undo : null,
        redo: enableUndo ? redo : null,
        canUndo: undoStack.length > 0,
        canRedo: redoStack.length > 0,

        // Permissions
        permissions: planLoading.permissions,
        canEdit: planLoading.permissions.canWrite !== false,
        canPublish: planLoading.permissions.canPublish === true,

        // Virtual plan handler
        virtualPlanHandler,

        // Schedule plan functionality
        schedulePlan
    };
};
